import React, { useEffect, useState } from 'react';
import Toast from './Toast';
import { useToast } from 'contexts/ToastContext';
import './Toast.scss';

const ToastContainer: React.FC = () => {
    const { toasts, removeToast } = useToast();
    const [positions, setPositions] = useState<Map<number, React.CSSProperties>>(new Map());

    useEffect(() => {
        const updatePositions = () => {
            const newPositions = new Map<number, React.CSSProperties>();

            toasts.forEach(toast => {
                if (toast.positioning?.targetRef?.current) {
                    const targetRect = toast.positioning.targetRef.current.getBoundingClientRect();
                    const placement = toast.positioning.placement || 'top';
                    const offset = toast.positioning.offset || 8;

                    // Temporarily increase target element's z-index if overlay is shown
                    if (toast.positioning.showOverlay) {
                        toast.positioning.targetRef.current.style.position = 'relative';
                        toast.positioning.targetRef.current.style.zIndex = '10000';
                    }

                    const position: React.CSSProperties = {
                        position: 'fixed',
                        zIndex: 9999
                    };

                    switch (placement) {
                        case 'top':
                            position.left = targetRect.left + targetRect.width / 2;
                            position.top = targetRect.top - offset;
                            position.transform = 'translate(-50%, -100%)';
                            break;
                        case 'bottom':
                            position.left = targetRect.left + targetRect.width / 2;
                            position.top = targetRect.bottom + offset;
                            position.transform = 'translate(-50%, 0)';
                            break;
                        case 'left':
                            position.top = targetRect.top + targetRect.height / 2;
                            position.left = targetRect.left - offset;
                            position.transform = 'translate(-100%, -50%)';
                            break;
                        case 'right':
                            position.top = targetRect.top + targetRect.height / 2;
                            position.left = targetRect.right + offset;
                            position.transform = 'translate(0, -50%)';
                            break;
                    }

                    newPositions.set(toast.id, position);
                }
            });

            setPositions(newPositions);
        };

        // Update positions initially and on scroll/resize
        updatePositions();
        window.addEventListener('scroll', updatePositions, true);
        window.addEventListener('resize', updatePositions);

        // Cleanup function to reset styles
        return () => {
            toasts.forEach(toast => {
                if (toast.positioning?.targetRef?.current && toast.positioning.showOverlay) {
                    toast.positioning.targetRef.current.style.position = '';
                    toast.positioning.targetRef.current.style.zIndex = '';
                }
            });
            window.removeEventListener('scroll', updatePositions, true);
            window.removeEventListener('resize', updatePositions);
        };
    }, [toasts]);

    if (toasts.length === 0) return null;


    return (
        <>
            {/* Simple overlay for the first positioned toast */}
            {toasts.some(toast => toast.positioning?.showOverlay) && (
                <div className="toast-overlay" />
            )}
            
            {toasts.map(toast => {
                if (toast.positioning?.targetRef) {
                    const position = positions.get(toast.id);
                    if (!position) return null;

                    return (
                        <div key={toast.id} style={position}>
                            <Toast
                                id={toast.id}
                                message={toast.message}
                                type={toast.type}
                                onClose={removeToast}
                                data-placement={toast.positioning.placement || 'top'}
                            />
                        </div>
                    );
                }

                return (
                    <div key={toast.id} className="toast-container">
                        <Toast
                            id={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={removeToast}
                        />
                    </div>
                );
            })}
        </>
    );
};

export default ToastContainer;
